/**
 * Short labels for the chat composer model trigger (narrow panels).
 */

type ChatModelOption = { id: string; name?: string; provider?: string }

/** Retired Anthropic picker ids mapped to current API ids. */
const ANTHROPIC_CHAT_MODEL_ALIASES: Record<string, string> = {
  'claude-sonnet-5': 'claude-sonnet-5-5',
  'claude-sonnet-4-6-20260220': 'claude-sonnet-5-5',
  'claude-sonnet-4-6': 'claude-sonnet-5-5',
  'claude-sonnet-4-5-20250929': 'claude-sonnet-5-5',
  'claude-opus-5': 'claude-opus-5-5',
  'claude-opus-4-8': 'claude-opus-5-5',
  'claude-opus-4-6': 'claude-opus-5-5',
  'claude-opus-4-5-20251101': 'claude-opus-5-5',
  'claude-haiku-4-5': 'claude-haiku-5-5',
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
}

export function normalizeAnthropicChatModelId(modelId: string): string {
  const trimmed = modelId?.trim() || ''
  return ANTHROPIC_CHAT_MODEL_ALIASES[trimmed] || trimmed
}

function flattenChatModels(
  availableModels: ChatModelOption[],
  allModelsByProvider: Record<string, ChatModelOption[]>,
): ChatModelOption[] {
  if (availableModels.length > 0) return availableModels
  return Object.values(allModelsByProvider).flat()
}

/** Ensure Select `value` matches a loaded option (Radix shows blank when it does not). */
export function resolveChatModelId(
  selectedModel: string,
  availableModels: ChatModelOption[],
  allModelsByProvider: Record<string, ChatModelOption[]>,
): string {
  const normalized = normalizeAnthropicChatModelId(selectedModel)
  const all = flattenChatModels(availableModels, allModelsByProvider)
  if (normalized && all.some((m) => m.id === normalized)) {
    return normalized
  }
  if (selectedModel && all.some((m) => m.id === selectedModel)) {
    return selectedModel
  }
  return all[0]?.id ?? selectedModel
}

export function fullChatModelDisplayName(params: {
  selectedModel: string
  availableModels: { id: string; name: string }[]
  allModelsByProvider: Record<string, { id: string; name: string }[]>
}): string {
  const { selectedModel, availableModels, allModelsByProvider } = params
  const fromAvailable = availableModels.find((m) => m.id === selectedModel)?.name
  if (fromAvailable) return fromAvailable
  for (const models of Object.values(allModelsByProvider)) {
    const hit = models.find((m) => m.id === selectedModel)?.name
    if (hit) return hit
  }
  return selectedModel
}

/**
 * Compress common Anthropic/OpenAI naming for toolbar width.
 */
export function shortChatModelDisplayName(fullName: string): string {
  let s = fullName.trim()
  // Chip trigger only - dropdown keeps full model.name including "(Recommended)".
  s = s.replace(/\s*\(Recommended\)\s*/gi, " ").trim()
  s = s.replace(/^Anthropic\s+/i, "")
  s = s.replace(/^OpenAI\s+/i, "")
  s = s.replace(/\bClaude\s+/gi, "")
  if (s.length > 22) return `${s.slice(0, 20)}...`
  return s || fullName
}
